import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, GitBranch } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../lib/api'
import type { Chat, Topic } from '../lib/apiModels'
import {
  isActive,
  jobTiming,
  SIDE_JOBS_POLL_MS,
  sideJobPath,
  sideJobsPath,
  STATE_LABEL,
  stripSummary,
  type SideJob,
  type SideJobsResponse,
} from '../lib/sideJobs'

const CHIP: Record<SideJob['state'], string> = {
  running: 'bg-teal-100 text-teal-900 dark:bg-teal-900/60 dark:text-teal-100',
  queued: 'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100',
  retry_wait: 'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100',
  dispatched_unconfirmed: 'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100',
  failed: 'bg-red-100 text-red-900 dark:bg-red-900/50 dark:text-red-100',
  stopped: 'bg-stone-200 text-stone-800 dark:bg-neutral-800 dark:text-neutral-200',
  done: 'bg-stone-200 text-stone-800 dark:bg-neutral-800 dark:text-neutral-200',
}

/**
 * Side jobs this conversation opened (`pipeline open-topic`), with their real state.
 *
 * Read-only: polls the side-jobs endpoint, whose states come from the side-topic
 * supervisor. Renders nothing when the conversation has no recent side jobs.
 */
export default function SideJobsStrip({
  chatId,
  chat,
  topic,
}: {
  chatId: string
  chat: Chat
  topic: Topic | null
}) {
  const [open, setOpen] = useState(false)
  const panelId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!open) return
    function dismiss(event: PointerEvent) {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) setOpen(false)
    }
    function escape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', dismiss)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('pointerdown', dismiss)
      document.removeEventListener('keydown', escape)
    }
  }, [open])
  const topicId = topic?.id ?? null
  const query = useQuery({
    queryKey: ['side_jobs', chatId, topicId],
    queryFn: () => api<SideJobsResponse>(sideJobsPath(chatId, topicId)),
    enabled: !chat.archived,
    refetchInterval: SIDE_JOBS_POLL_MS,
    staleTime: 15_000,
  })
  const jobs = query.data?.jobs ?? []
  if (jobs.length === 0) return null
  const nowSeconds = Date.now() / 1000
  const anyActive = jobs.some(isActive)

  return (
    <div ref={rootRef} className="relative" data-testid="side-jobs-strip">
      <button
        type="button"
        ref={buttonRef}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={`Side jobs: ${stripSummary(jobs)}`}
        title={stripSummary(jobs)}
        onPointerDown={(event) => event.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        className={`min-h-11 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs border ${
          anyActive
            ? 'border-teal-300 bg-teal-50 text-teal-950 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-100'
            : 'border-stone-300 bg-stone-50 text-stone-800 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200'
        }`}
      >
        <span className="flex items-center gap-2 font-medium">
          <GitBranch className="h-4 w-4 shrink-0" aria-hidden="true" />
          Side jobs
        </span>
        <span className="tabular-nums">{jobs.length}</span>
        {open ? <ChevronUp className="h-3 w-3" aria-hidden="true" /> : <ChevronDown className="h-3 w-3" aria-hidden="true" />}
      </button>
      {open && (
        <div id={panelId} role="region" aria-label="Side jobs" className="absolute bottom-full left-0 z-40 mb-1 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-stone-200 bg-white shadow-lg dark:border-neutral-800 dark:bg-neutral-950">
          <p className="px-4 py-2 text-xs text-stone-600 dark:text-neutral-300">{stripSummary(jobs)}</p>
          <ul className="text-sm divide-y divide-stone-100 dark:divide-neutral-800 max-h-[min(18rem,45dvh)] overflow-y-auto">
          {jobs.map((job) => (
            <li key={job.topic_id}>
              <Link
                to={sideJobPath(job)}
                className="flex min-h-11 items-start justify-between gap-3 px-4 py-2 text-stone-800 dark:text-neutral-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-600"
              >
                <span className="min-w-0">
                  <span className="block break-words">{job.title}</span>
                  <span className="block text-xs text-stone-500 dark:text-neutral-400">{jobTiming(job, nowSeconds)}</span>
                </span>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${CHIP[job.state]}`}>
                  {STATE_LABEL[job.state]}
                </span>
              </Link>
            </li>
          ))}
          </ul>
        </div>
      )}
    </div>
  )
}
